import ApiService from "@/core/services/ApiService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";
import {AxiosResponse} from "axios";
import type {CourseData} from "@/types/CourseType";
import {LessonType} from "@/types/LessonType";
import {Quiz} from "@/types/QuizType";
import {Student} from "@/types/StudentType";
import posthog from '@/posthog';

interface Statistics{
    total_lessons: number;
    total_completed_lessons: number;
}

interface CourseFilters {
    name: string | null;
    types: string[];
}

@Module
export default class CourseModule extends VuexModule {

    errros = {};
    courseData: CourseData = {} as CourseData;
    currentCourse: CourseData = {} as CourseData;
    statistics: Statistics = {} as Statistics;
    courseAccessDenied: boolean = false;
    courseFilters: CourseFilters = { name: null, types: [] };

    get getCurrentCourse() {
        return this.currentCourse;
    }

    get getStatistics() {
        return this.statistics;
    }

    get getCourseAccessDenied() {
        return this.courseAccessDenied;
    }

    get getCourseFilters(): CourseFilters {
        return this.courseFilters;
    }

    get getCourseData(): CourseData {
        return this.courseData;
    }
    get getLessons(): LessonType[] {
        return this.courseData.lessons;
    }
    get getQuiz(): Quiz {
        return this.courseData.quiz;
    }
    get getStudent(): Student {
        return this.courseData.Student;
    }

    @Action
    async [Actions.GET_ALL_COURSES]() {

        try {
            const response: AxiosResponse<any> = await ApiService.get("/academy/courses/list");
            this.context.commit(Mutations.SET_COURSE_DATA, response.data);
            posthog.capture('viewed_courses', {});
        } catch (error) {
            this.context.commit(Mutations.SET_COURSE_DATA, {});
        }
    }
    
    @Action
    async [Actions.GET_FILTERED_COURSES](filters: any) {
        try {
            // Construir query params
            const params = new URLSearchParams();
            
            if (filters.filters.name) {
                params.append('name', filters.filters.name);
            }
            
            if (filters.filters.types && filters.filters.types.length > 0) {
                filters.filters.types.forEach((type: string) => {
                    params.append('types[]', type);
                });
            }
            
            const queryString = params.toString();
            const url = queryString ? `/academy/courses/list?${queryString}` : '/academy/courses/list';
            
            const response: AxiosResponse<any> = await ApiService.get(url);
            this.context.commit(Mutations.SET_COURSE_DATA, response.data);
            posthog.capture('filtered_courses', { filters });
        } catch (error) {
            this.context.commit(Mutations.SET_COURSE_DATA, {});
        }
    }
    
    @Action
    async [Actions.GET_COURSE](id: string) {
        this.context.commit(Mutations.SET_COURSE_ACCESS_DENIED, false);
        
        try {
            const response: AxiosResponse<any> = await ApiService.get("/academy/courses/"+id);
            this.context.commit(Mutations.SET_CURRENT_COURSE_DATA, response.data.data);
            posthog.capture('viewed_course', {
              id: id,
              name: response.data.data?.name,
              is_premium: response.data.data?.is_premium,
              user_status: response.data.data?.user_status
            });
        } catch (error: any) {
            if (error.response && error.response.status === 402) {
                // Erro 402 significa que precisa de pagamento
                this.context.commit(Mutations.SET_COURSE_ACCESS_DENIED, true);
            } else {
                this.context.commit(Mutations.SET_CURRENT_COURSE_DATA, {});
            }
        }
        
        // Só busca estatísticas se não for erro 402
        if (!this.courseAccessDenied) {
            try {
                const response: AxiosResponse<any> = await ApiService.get("/academy/courses/"+id+"/statistics");
                this.context.commit(Mutations.SET_STATISTICS, response.data.data);
            } catch (error: any) {
                if (error.response && error.response.status === 402) {
                    // Erro 402 nas estatísticas também
                    this.context.commit(Mutations.SET_COURSE_ACCESS_DENIED, true);
                }
                this.context.commit(Mutations.SET_STATISTICS, {});
            }
        }
    }
    @Mutation
    [Mutations.SET_COURSE_DATA](courseData: CourseData) {
        if(courseData.lessons) {
            this.courseData.lessons = courseData.lessons;
        }

        this.courseData = courseData;
    }

    @Mutation
    [Mutations.SET_CURRENT_COURSE_DATA](course: CourseData) {
        this.currentCourse = course;
    }

    @Mutation
    [Mutations.SET_STATISTICS](statistics: Statistics) {
        this.statistics = statistics;
    }

    @Mutation
    [Mutations.SET_COURSE_ACCESS_DENIED](denied: boolean) {
        this.courseAccessDenied = denied;
    }


    @Mutation
    [Mutations.SET_COURSE_FILTERS](filters: CourseFilters) {
        this.courseFilters = filters;
    }

}
